import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUp, Mic, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/hooks/useAuth";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";
import SatelliteButton from "@/components/v2/SatelliteButton";
import PrimaryActions from "@/components/v2/PrimaryActions";
import GlassSheet from "@/components/v2/GlassSheet";
import UserAvatar from "@/components/UserAvatar";
import { parseKrishnaAnswer } from "@/lib/parseAnswer";
import { useAuth } from "@/hooks/useAuth";
import TempleRingButton from "@/components/v2/TempleRingButton";
import DharmaMeter from "@/components/v2/DharmaMeter";
import SankalpSheet from "@/components/v2/SankalpSheet";
import PrasadSheet from "@/components/v2/PrasadSheet";
import BlessingSheet from "@/components/v2/BlessingSheet";
import PrasadSemicircle from "@/components/v2/PrasadSemicircle";
import CreditsBadge from "@/components/v2/CreditsBadge";
import BirthIntakeChat from "@/components/v2/BirthIntakeChat";
import ProfileDropdown from "@/components/kalki/ProfileDropdown";
import WalletSheet from "@/components/kalki/WalletSheet";
import LifeJourneyTimeline from "@/components/kalki/LifeJourneyTimeline";
import KalkiIntelligenceDock, { INTEL_LAYERS } from "@/components/kalki/KalkiIntelligenceDock";
import KalkiPurpleSheet from "@/components/kalki/KalkiPurpleSheet";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = (() => {
  if (!BACKEND_URL) return "";
  try {
    const u = new URL(BACKEND_URL);
    u.protocol = u.protocol === "https:" ? "wss:" : "ws:";
    u.pathname = "/api/ws/krishna";
    return u.toString();
  } catch (e) { return ""; }
})();

const SATELLITES_LEFT = [
  { id: "character", label: "Character" },
  { id: "karma", label: "Karma" },
  { id: "journey", label: "Journey" },
];
const SATELLITES_RIGHT = [
  { id: "future", label: "Future" },
  { id: "why", label: "Why" },
  { id: "remedies", label: "Remedies" },
  { id: "me", label: "Me" },
];

export default function KalkiV2() {
  const navigateTo = useNavigate();
  const { user, loading, login, logout } = useAuth();
  const [earthState, setEarthState] = useState("idle");
  const [talkOpen, setTalkOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [activeSheet, setActiveSheet] = useState(null); // satellite id
  const [walletOpen, setWalletOpen] = useState(false);

  // Global dropdown → open Wallet (dispatched from KalkiGlobalHeader > ProfileDropdown)
  useEffect(() => {
    const h = () => setWalletOpen(true);
    window.addEventListener("kalki:open-wallet", h);
    return () => window.removeEventListener("kalki:open-wallet", h);
  }, []);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [dharma, setDharma] = useState({ score: 20, state: "Seeking" });
  const [blessingsAvailable, setBlessingsAvailable] = useState(0);
  const [templeSheet, setTempleSheet] = useState(null); // sankalp | prasad | blessing | bell
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const [chartSummary, setChartSummary] = useState(null); // {lagna, moon, md}
  const [creditsRefresh, setCreditsRefresh] = useState(0);
  const [templeHubOpen, setTempleHubOpen] = useState(false);
  const [draftResume, setDraftResume] = useState(null); // { draft_id, step } if an unfinished Life Report exists
  const [reportsLib, setReportsLib] = useState([]);     // list of generated reports for Me sheet
  const scrollRef = useRef(null);

  // Load Life Report draft + library whenever user present
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/report/library");
        setReportsLib(data?.reports || []);
      } catch (e) { /* soft fail */ }
      try {
        // start returns the draft (resumes if one exists)
        const { data } = await api.post("/report/start");
        if (data && data.draft_id && data.step < 6) {
          // consider as "resume" only if user has supplied birth already
          const hasBirth = !!(data.profile_birth && data.profile_birth.date);
          if (hasBirth || data.step > 1) {
            setDraftResume({ draft_id: data.draft_id, step: data.step });
          }
        }
      } catch (e) { /* soft fail */ }
    })();
  }, [user, creditsRefresh]);

  // Refresh Dharma + blessings + chart whenever a user is present
  useEffect(() => {
    if (!user) return;
    // One-time 1000-credit special toast
    if (typeof window !== "undefined" && sessionStorage.getItem("kalki_special_1000") === "1") {
      sessionStorage.removeItem("kalki_special_1000");
      toast.success("✦ 1,000 KALKI Credits — your KALKI credits have been added.", { duration: 7000 });
      setCreditsRefresh((n) => n + 1);
    }
  }, [user]);

  // Post-signup onboarding gate — if a signed-in user has NOT completed the
  // mandatory 5-step "Your Beginning" flow, send them there before anything
  // else. The optional 7-step enrichment is NEVER auto-launched — the user
  // opens it from the "+ Add More About My Life" button on this page.
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/profile");
        if (r.data && r.data.profile_complete === false) {
          navigateTo("/onboarding/your-beginning", { replace: true });
        }
      } catch (_) { /* ignore */ }
    })();
  }, [user, navigateTo]);

  // Refresh Dharma + blessings + chart whenever a user is present
  useEffect(() => {
    if (!user) { setDharma({ score: 20, state: "Seeking" }); setBlessingsAvailable(0); setChartReady(false); setChartSummary(null); return; }
    (async () => {
      try {
        const d = await api.get("/dharma"); setDharma(d.data || { score: 20, state: "Seeking" });
        const b = await api.get("/blessings"); setBlessingsAvailable((b.data.available || []).length);
        const c = await api.get("/vedic/chart");
        setChartReady(!!c.data.ready);
        if (c.data.chart) {
          const ch = c.data.chart;
          const vmd = ch?.dashas?.vimshottari || [];
          const today = new Date().toISOString().slice(0, 10);
          const cur = vmd.find((m) => m.start_date <= today && today < m.end_date);
          setChartSummary({
            lagna: `${ch?.lagna?.sign} ${ch?.lagna?.pos || ""}`,
            moon_nak: ch?.planets?.Moon?.nakshatra,
            md: cur ? `${cur.lord} (until ${cur.end_date})` : null,
          });
        }
      } catch (e) {}
    })();
  }, [user, templeSheet, intakeOpen, creditsRefresh]);

  const ringBell = async () => {
    toast.success("🔔 I am here.");
    if (user) { try { await api.post("/bell"); } catch (e) {} }
  };

  const voice = { state: "idle", stop: () => {}, startListening: () => {}, partial: "", fallbackText: "", playbackBlocked: false, resumePlayback: () => {}, supportsSpeech: true };

  useEffect(() => { setEarthState("idle"); }, []);

  useEffect(() => {
    if (!chatOpen) return;
    if (scrollRef.current) scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, chatOpen]);

  const sendMessage = async (q) => {
    const question = (q ?? input).trim();
    if (!question || sending) return;
    setInput("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", text: question }]);
    setSending(true);
    setEarthState("thinking");
    try {
      const { data } = await api.post("/ask", { question });
      const parsed = parseKrishnaAnswer(data.answer || "");
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "ai", text: data.answer, parsed }]);
    } catch (e) {
      toast.error("KALKI is quiet for a moment. Please try again.");
    } finally {
      setSending(false);
      setEarthState("idle");
    }
  };

  const openTalk = () => {
    // New isolated, reliable voice experience lives at /app/talk.
    navigateTo("/app/talk");
  };
  const closeTalk = () => { voice.stop(); setTalkOpen(false); };

  const greeting = useMemo(() => {
    const first = user?.name?.split(" ")[0];
    return first
      ? `Welcome, ${first}. I am here with you.`
      : "I am KALKI, your friend. I am here with you.";
  }, [user]);

  return (
    <div className="relative w-full text-slate-900 flex flex-col kalki-brand"
         style={{ background: "#FAFAFC", backgroundImage: "radial-gradient(#DCE2EA 1px, transparent 1px)", backgroundSize: "24px 24px", minHeight: "100dvh" }}>
      {/* Dark cinematic intro REMOVED — no dark theme traces post-login */}
      <style>{`.kalki-brand{--brand-indigo:#5D00FF;--brand-fuchsia:#FF2E9A;--brand-lime:#CFFF04;--brand-graphite:#1C1C1C;--brand-mute:#676773}
        .kalki-brand .fuchsia-grad{background:linear-gradient(135deg,var(--brand-indigo),var(--brand-fuchsia))}
        .kalki-brand button[data-brand=primary]{background:linear-gradient(140deg,var(--brand-indigo),var(--brand-fuchsia));border:1px solid rgba(207,255,4,.4) !important;color:#fff}
        .kalki-brand .k-ghost{position:absolute;font-family:"Cormorant Garamond","Times New Roman",Georgia,serif;font-weight:300;letter-spacing:.04em;color:var(--brand-graphite);opacity:.045;font-size:clamp(220px,28vw,420px);line-height:.8;pointer-events:none;user-select:none}
        .kalki-brand .k-glass{background:rgba(255,255,255,.55);backdrop-filter:blur(22px) saturate(140%);-webkit-backdrop-filter:blur(22px) saturate(140%);border:1px solid rgba(255,255,255,.75);border-radius:28px;box-shadow:0 20px 60px rgba(30,20,70,.08)}
        .kalki-brand .k-chip{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:999px;font-size:12px;letter-spacing:.08em;background:rgba(255,255,255,.65);backdrop-filter:blur(14px);border:1px solid rgba(0,0,0,.06);color:var(--brand-graphite);cursor:pointer;transition:all .2s}
        .kalki-brand .k-chip:hover{border-color:var(--brand-indigo);color:var(--brand-indigo);transform:translateY(-1px)}
      `}</style>

      {/* Minimal floating header — unified KalkiGlobalHeader (Website 6.0) */}
      <KalkiGlobalHeader />

      {/* Primary stage — single-column centered. Krishna image removed.
          Scrollable when content exceeds viewport (fixes KalkiV2 overflow).
          pb accounts for the ~100px-tall Intelligence Dock + its 14px gap + safe-area so
          Relevant Now never slides under the dock. */}
      <main
        className="relative z-10 flex-1 flex flex-col items-center justify-center max-w-[720px] w-full mx-auto px-6 sm:px-10 pt-6"
        style={{
          paddingBottom: "calc(140px + env(safe-area-inset-bottom))",
        }}
      >
        {/* Greeting + What's on your mind + Talk/Chat + Life Report */}
        <section className="relative z-10 flex flex-col gap-6 w-full text-center items-center my-auto">
          <div className="text-[11px] tracking-[0.3em] uppercase text-[#676773]" data-testid="post-login-greeting">
            {user ? `${greeting}${user?.name ? `, ${user.name.split(" ")[0]}` : ""}` : "Welcome · Life Coach + AI Astrologer"}
          </div>

          {/* User DP (2x) + name + ongoing karma text — anchors the home
              screen to who the user is right now. */}
          {user && (
            <div className="flex flex-col items-center gap-2" data-testid="home-user-anchor">
              <span
                className="rounded-full overflow-hidden"
                style={{
                  width: 72, height: 72,
                  background: user.picture
                    ? `url(${user.picture}) center/cover`
                    : "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                  border: "2px solid rgba(255,255,255,0.8)",
                  boxShadow: "0 12px 36px rgba(93,0,255,0.18)",
                }}
                data-testid="home-user-dp"
                aria-hidden
              />
              <div className="text-[14px] text-[#1C1C1C] font-medium" data-testid="home-user-name">
                {user.name || user.email?.split("@")[0]}
              </div>
              <div className="text-[11px] tracking-[0.12em] text-[#676773]" data-testid="home-user-karma">
                Your ongoing karma is unfolding
              </div>
            </div>
          )}

          <h1 className="font-serif font-light leading-[1.02] text-[#1C1C1C]" style={{ fontSize: "clamp(44px,6.5vw,92px)" }}>
            What's on<br />
            <em className="not-italic" style={{ background: "linear-gradient(135deg,#5D00FF,#FF2E9A)", WebkitBackgroundClip: "text", color: "transparent" }}>your mind?</em>
          </h1>
          <p className="text-[15px] text-[#676773] max-w-[42ch] leading-relaxed mx-auto">Talk to me about anything — life, timing, a decision you're sitting with.</p>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            {/* Mobile fix (Oct 2026) — when not signed in, Chat/Talk show a
                sign-in screen. Hint it clearly before the user taps a button
                they expect to open immediately. Shown on all widths; the top
                header pill is easy to miss on phones. */}
            {!loading && !user && (
              <button
                data-testid="hero-signin-pill"
                onClick={login}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[12.5px] font-medium text-white mb-1"
                style={{
                  background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                  boxShadow: "0 10px 24px rgba(93,0,255,0.25)",
                  letterSpacing: "0.04em",
                }}
              >
                Sign in to Talk or Chat
              </button>
            )}
            <button data-testid="primary-talk" onClick={openTalk} data-brand="primary"
                    className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-[14.5px] font-medium shadow-[0_20px_50px_rgba(93,0,255,0.3)] transition-transform hover:-translate-y-0.5">
              <Mic className="w-4 h-4" /> Talk
            </button>
            <button data-testid="primary-chat" onClick={() => navigateTo("/app/chat")}
                    className="inline-flex items-center gap-2 px-6 py-4 rounded-full text-[14px] text-[#1C1C1C] border border-black/10 bg-white/70 hover:border-[#5D00FF] hover:text-[#5D00FF] transition-all">
              💬 Chat
            </button>
          </div>

          {/* Life Report entry — premium secondary action */}
          <a href="/app/report" data-testid="home-life-report"
             className="inline-flex items-center gap-3 mt-2 p-3 pr-5 rounded-full max-w-fit mx-auto no-underline hover:-translate-y-0.5 transition-transform"
             style={{ background: "rgba(255,255,255,.75)", border: "1px solid rgba(93,0,255,.2)", backdropFilter: "blur(14px)", boxShadow: "0 10px 28px rgba(93,0,255,.15)" }}>
            <span className="px-3 py-1.5 rounded-full text-[9.5px] font-bold tracking-[0.25em] text-white" style={{ background: "linear-gradient(135deg,#5D00FF,#FF2E9A)" }}>✦ NEW</span>
            <span className="flex flex-col leading-tight">
              <span className="text-[13px] tracking-[0.14em] font-semibold text-[#1C1C1C]">LIFE REPORT</span>
              <span className="text-[11px] text-[#676773] tracking-[0.02em]">Full Vedic Life Report · 500 Credits</span>
            </span>
            <span className="text-[#FF2E9A] text-[16px]">→</span>
          </a>

          {/* Prompt chips */}
          <div className="flex flex-wrap gap-2 pt-2" data-testid="prompt-chips">
            {["Life","Career","Love","Money","Purpose","What's ahead?"].map((p) => (
              <button key={p} className="k-chip" onClick={() => {
                const q = p === "What's ahead?" ? "Tell me about what may lie ahead." : `Let's talk about ${p.toLowerCase()}.`;
                navigateTo(`/app/chat?prompt=${encodeURIComponent(q)}`);
              }}>
                {p}
              </button>
            ))}
          </div>

          {/* Relevant Now — only shows when there's genuine context. Extra
              bottom margin ensures the last card never slips under the dock. */}
          {user && (
            <div className="mt-10 mb-8 flex flex-col gap-2 max-w-[460px] w-full mx-auto" data-testid="relevant-now">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#676773]">Relevant now</div>
              {/* Draft resume — top priority */}
              {draftResume && (
                <a href="/app/report" data-testid="relevant-resume"
                   className="k-glass flex items-start gap-3 px-4 py-3 text-left no-underline hover:scale-[1.01] transition-transform"
                   style={{ background: "rgba(255,255,255,.78)", border: "1px solid rgba(93,0,255,.25)" }}>
                  <span style={{ color: "#FF2E9A" }}>✦</span>
                  <div className="flex-1">
                    <div className="text-[11px] tracking-[0.2em] text-[#676773] uppercase">Continue your Life Report</div>
                    <div className="text-[13.5px] text-[#1C1C1C] font-medium">{draftResume.step} of 6 complete · tap to continue</div>
                  </div>
                  <span className="text-[#5D00FF]">→</span>
                </a>
              )}
              {chartReady && chartSummary?.md && (
                <button className="k-glass flex items-start gap-3 px-4 py-3 text-left hover:scale-[1.01] transition-transform" onClick={() => setActiveSheet("future")}>
                  <span style={{ color: "#5D00FF" }}>✦</span>
                  <div className="flex-1">
                    <div className="text-[11px] tracking-[0.2em] text-[#676773] uppercase">Your timing</div>
                    <div className="text-[13.5px] text-[#1C1C1C] font-medium">Active Mahadasha · {chartSummary.md}</div>
                  </div>
                </button>
              )}
              {blessingsAvailable > 0 && (
                <button className="k-glass flex items-start gap-3 px-4 py-3 text-left hover:scale-[1.01] transition-transform" onClick={() => setTempleSheet("blessing")}>
                  <span style={{ color: "#FF2E9A" }}>✨</span>
                  <div className="flex-1">
                    <div className="text-[11px] tracking-[0.2em] text-[#676773] uppercase">A blessing awaits</div>
                    <div className="text-[13.5px] text-[#1C1C1C] font-medium">Tap to receive it.</div>
                  </div>
                </button>
              )}
              {!chartReady && (
                <button className="k-glass flex items-start gap-3 px-4 py-3 text-left hover:scale-[1.01] transition-transform" onClick={() => setIntakeOpen(true)}>
                  <span style={{ color: "#CFFF04", filter: "drop-shadow(0 0 6px #CFFF04)" }}>◌</span>
                  <div className="flex-1">
                    <div className="text-[11px] tracking-[0.2em] text-[#676773] uppercase">Share your stars</div>
                    <div className="text-[13.5px] text-[#1C1C1C] font-medium">Unlock Future, Why and Remedies.</div>
                  </div>
                </button>
              )}
              {/* v0.6 Phase B — Optional "+ Add More About My Life" button
                  always surfaces the 7-step enrichment form. Never auto-launched. */}
              <button
                data-testid="add-more-life-btn"
                className="k-glass flex items-start gap-3 px-4 py-3 text-left hover:scale-[1.01] transition-transform"
                onClick={() => navigateTo("/onboarding/deepen")}>
                <span style={{ color: "#5D00FF" }}>+</span>
                <div className="flex-1">
                  <div className="text-[11px] tracking-[0.2em] text-[#676773] uppercase">Add More About My Life</div>
                  <div className="text-[13.5px] text-[#1C1C1C] font-medium">The more KALKI understands your journey, the more personal its guidance becomes.</div>
                </div>
              </button>
            </div>
          )}
        </section>

        {/* Floating KALKI Intelligence Dock — premium purple glass with 6 iconic layers */}
      </main>

      <KalkiIntelligenceDock
        onOpen={(id) => setActiveSheet(id)}
        active={activeSheet}
        user={user}
        badges={{
          karma: null,
          future: chartReady ? "fuchsia" : null,
          why: null,
          me: blessingsAvailable > 0 ? "fuchsia" : null,
        }}
      />

      {/* CHAT bottom-sheet REMOVED — chat is now a full page at /app/chat */}

      {/* TALK overlay REMOVED — Talk is now a dedicated page at /app/talk */}

      {/* Satellite sheets */}
      <SatelliteSheet
        id={activeSheet}
        user={user}
        onClose={() => setActiveSheet(null)}
        onLogin={login}
        onLogout={logout}
        chartReady={chartReady}
        chartSummary={chartSummary}
        onIntake={() => { setActiveSheet(null); setIntakeOpen(true); }}
        onCreditsChanged={() => setCreditsRefresh((n) => n + 1)}
        reportsLib={reportsLib}
      />

      {/* v0.6 Phase A — Wallet (packages, coupons, referral) */}
      <WalletSheet open={walletOpen} onClose={() => { setWalletOpen(false); setCreditsRefresh((n) => n + 1); }} />

      {/* Birth-intake conversational flow */}
      <BirthIntakeChat
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        user={user}
        onComplete={() => { setCreditsRefresh((n) => n + 1); setChartReady(true); }}
      />

      {/* Temple sheets */}
      <SankalpSheet open={templeSheet === "sankalp"} onClose={() => setTempleSheet(null)} user={user} />
      <PrasadSheet open={templeSheet === "prasad"} onClose={() => setTempleSheet(null)} user={user} />
      <BlessingSheet open={templeSheet === "blessing"} onClose={() => setTempleSheet(null)} user={user} onOpened={() => setBlessingsAvailable(Math.max(0, blessingsAvailable - 1))} />

      {/* Mobile-only extra: ambient reduced */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { scrollbar-width: none; }
      `}</style>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Satellite sheet content (iter-7: real predictions when chart ready)
// -----------------------------------------------------------------------------
function SatelliteSheet({ id, user, onClose, onLogin, onLogout, chartReady, chartSummary, onIntake, onCreditsChanged, reportsLib }) {
  if (!id) return null;

  const needsAuth = !user;
  const authGate = (
    <div className="space-y-4">
      <p className="leading-relaxed" style={{ color: "rgba(230,220,255,0.75)" }}>
        To walk this part of the path together, I need to know who you are. Sign in once and I will remember you across every conversation.
      </p>
      <button onClick={onLogin} data-testid="sheet-login-btn"
        className="w-full rounded-full py-3 text-[14px] font-medium text-slate-50"
        style={{ background: "linear-gradient(140deg,#5D00FF,#FF2E9A)", border: "1px solid rgba(255,255,255,0.2)" }}>
        Continue with Google
      </button>
      <a href="/?signin=1" data-testid="sheet-create-account-btn"
        className="block w-full text-center rounded-full py-3 text-[13px]"
        style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)", color: "rgba(230,220,255,0.9)" }}>
        Create account with email
      </a>
      <div className="text-center text-[11px] tracking-[0.18em] uppercase" style={{ color: "rgba(230,220,255,0.5)" }}>
        Already have an account? <a href="/?signin=1" style={{ color: "#D8C7FF" }}>Sign in</a>
      </div>
    </div>
  );

  const layer = INTEL_LAYERS.find((l) => l.id === id);
  const icon = layer?.icon || "✦";
  const title = layer?.title || "KALKI";
  const subtitle = layer?.subtitle || "";
  const heightClass = (id === "future" || id === "why") ? "max-h-[90dvh] md:max-h-[85dvh]" : "max-h-[82dvh] md:max-h-[78dvh]";

  return (
    <KalkiPurpleSheet
      open={!!id}
      onClose={onClose}
      icon={icon}
      eyebrow="KALKI INTELLIGENCE"
      title={title}
      subtitle={subtitle}
      heightClass={heightClass}
      testid={`sheet-${id}`}
    >
      {needsAuth && authGate}
      {!needsAuth && <SatelliteBody id={id} user={user} chartReady={chartReady} chartSummary={chartSummary} onIntake={onIntake} onCreditsChanged={onCreditsChanged} reportsLib={reportsLib} onLogout={onLogout} />}
    </KalkiPurpleSheet>
  );
}

function SatelliteBody({ id, user, chartReady, chartSummary, onIntake, onCreditsChanged, reportsLib, onLogout }) {
  if (id === "character") {
    if (!chartReady) return <LockedTile line="I am still learning your nature from our conversations. Share your birth time and I can show you the signature of your soul." onIntake={onIntake} />;
    return <CharacterTile summary={chartSummary} />;
  }
  if (id === "future") {
    if (!chartReady) return <LockedTile line="KALKI is still mapping the stars — share your exact birth time and place to unlock the road I see ahead." onIntake={onIntake} />;
    return <PredictionsTile domain="future" onCreditsChanged={onCreditsChanged} />;
  }
  if (id === "why") {
    if (!chartReady) return <LockedTile line="KALKI is still mapping the stars — share your birth details and I will show you the reasons behind the timing." onIntake={onIntake} />;
    return <WhyTile onCreditsChanged={onCreditsChanged} />;
  }
  if (id === "remedies") {
    if (!chartReady) return <LockedTile line="Once I understand your timing, I will suggest small daily practices suited to this phase of your life." onIntake={onIntake} />;
    return <RemediesTile />;
  }
  if (id === "karma") return <KarmaBody />;
  if (id === "journey") return <LifeJourneyTimeline user={user} />;
  if (id === "me") return <MeTile user={user} reportsLib={reportsLib} onLogout={onLogout} />;
  return null;
}

function MeTile({ user, reportsLib, onLogout }) {
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
  };
  const isIOS = typeof window !== "undefined"
    && /iPad|iPhone|iPod/.test(navigator.userAgent)
    && !window.MSStream;
  const [iosHintFor, setIosHintFor] = useState(null);

  const openPdf = (id) => {
    // Universal VIEW endpoint — inline Content-Disposition, cookie-authed.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/report/${id}/view`;
    window.open(url, "_blank", "noopener");
  };
  const downloadPdf = (id) => {
    if (isIOS) {
      // iOS Safari ignores <a download>. Open inline, show Save hint.
      setIosHintFor(id);
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/report/${id}/view`, "_blank", "noopener");
      return;
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/report/${id}/download`;
    const a = document.createElement("a");
    a.href = url;
    a.setAttribute("download", `KALKI_Life_Report_${id.slice(0, 8)}.pdf`);
    a.setAttribute("target", "_blank");
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <div className="space-y-6" data-testid="me-tile">
      <div className="flex items-center gap-4">
        {user?.picture && <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />}
        <div>
          <div className="text-[18px] font-serif text-[#1C1C1C]">{user?.name || "Devotee"}</div>
          <div className="text-[12px] text-[#676773]">{user?.email}</div>
        </div>
      </div>

      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#676773] mb-3">My Life Reports</div>
        {(!reportsLib || reportsLib.length === 0) ? (
          <div className="rounded-2xl border border-black/5 bg-black/[0.025] px-5 py-6 text-center" data-testid="me-reports-empty">
            <div className="text-[13px] text-[#676773] leading-relaxed">You haven't generated a Life Report yet.</div>
            <a href="/app/report" className="inline-block mt-4 px-5 py-2.5 rounded-full text-[13px] text-white"
               style={{ background: "linear-gradient(135deg,#5D00FF,#FF2E9A)" }}>
              Create my Life Report → 500 Credits
            </a>
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="me-reports-list">
            {reportsLib.map((r) => (
              <div key={r.id} className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white/70 px-4 py-3" data-testid={`me-report-${r.id}`}>
                <span className="text-[#FF2E9A]">✦</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium text-[#1C1C1C]">Life Report · {fmt(r.created_at)}</div>
                  <div className="text-[11px] text-[#676773]">{r.credit_cost} Credits · {r.status}</div>
                </div>
                <button onClick={() => openPdf(r.id)} data-testid={`me-report-open-${r.id}`}
                        className="text-[12px] tracking-[0.08em] text-[#1C1C1C] hover:text-[#5D00FF] whitespace-nowrap px-2 py-1">
                  Open
                </button>
                <button onClick={() => downloadPdf(r.id)} data-testid={`me-report-dl-${r.id}`}
                        className="text-[12px] tracking-[0.08em] text-[#5D00FF] hover:text-[#FF2E9A] whitespace-nowrap px-2 py-1">
                  Download →
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <InviteFriendTile />

      <MyRemediesList />

      <div className="pt-4 border-t border-black/5">
        <button onClick={onLogout} className="text-[12px] text-[#676773] hover:text-[#1C1C1C] tracking-[0.08em]" data-testid="me-logout">
          Sign out
        </button>
      </div>

      {/* iOS Save-to-Files coachmark */}
      {iosHintFor && (
        <div
          data-testid="me-ios-save-hint"
          onClick={() => setIosHintFor(null)}
          style={{
            position: "fixed", inset: 0, zIndex: 1100,
            background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "center",
          }}
          role="dialog" aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#F8F3EC", color: "#1C1C1C",
              borderTopLeftRadius: 24, borderTopRightRadius: 24,
              padding: "22px 22px calc(22px + env(safe-area-inset-bottom))",
              maxWidth: 460, width: "100%",
              boxShadow: "0 -24px 60px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{fontSize: 10, letterSpacing: "0.32em", color: "#5D00FF", marginBottom: 8}}>
              SAVE TO FILES
            </div>
            <h3 style={{fontSize: 20, margin: "0 0 10px 0", fontWeight: 500}}>
              Save your Life Report to Files
            </h3>
            <p style={{fontSize: 14, lineHeight: 1.5, color: "#434353", margin: "0 0 16px 0"}}>
              1. Your PDF just opened in Safari.<br/>
              2. Tap the <strong>Share</strong> icon (square with arrow).<br/>
              3. Choose <strong>Save to Files</strong> → <strong>On My iPhone</strong>.
            </p>
            <button
              onClick={() => setIosHintFor(null)}
              style={{
                width: "100%", background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                color: "white", border: 0, padding: "14px 18px", borderRadius: 999,
                fontSize: 13, letterSpacing: "0.2em", fontWeight: 500, cursor: "pointer",
              }}
            >GOT IT</button>
          </div>
        </div>
      )}
    </div>
  );
}

function InviteFriendTile() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/referrals/me"); setData(r.data); } catch (_) {}
    })();
  }, []);
  if (!data?.enabled) return null;
  const url = data.share_url;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1600); } catch (_) {}
  };
  const share = async () => {
    const text = `Begin your journey with KALKI. 100 Welcome + 50 Referral Credits. ${url}`;
    if (navigator.share) { try { await navigator.share({ title: "KALKI", text, url }); return; } catch (_) {} }
    copy();
  };
  const rewarded = data.counts?.REWARDED || 0;
  const pending  = (data.counts?.PENDING || 0) + (data.counts?.SIGNED_UP || 0) + (data.counts?.ONBOARDING_COMPLETE || 0) + (data.counts?.FIRST_INTERACTION_COMPLETE || 0);
  return (
    <div className="rounded-2xl border border-[#5D00FF]/15 bg-gradient-to-br from-[#5D00FF]/[0.04] to-[#FF2E9A]/[0.03] px-5 py-5" data-testid="invite-friend-tile">
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#5D00FF]/80 mb-2">Invite a Friend</div>
      <div className="text-[14px] text-[#1C1C1C] font-medium">Share KALKI. Earn 50 Credits.</div>
      <p className="text-[12px] text-[#676773] mt-1 leading-relaxed">Your friend gets 50 referral + 100 welcome Credits after they complete onboarding and ask KALKI their first question.</p>

      <div className="mt-3 flex items-stretch gap-2">
        <input readOnly value={url} className="flex-1 rounded-full border border-black/10 bg-white/70 px-3 py-2 text-[12px] text-[#1C1C1C] font-mono tracking-tight" data-testid="invite-url"/>
        <button onClick={copy} className="rounded-full px-3 py-2 text-[11px] tracking-[0.1em] uppercase text-white"
                style={{ background: "linear-gradient(135deg,#5D00FF,#FF2E9A)" }} data-testid="invite-copy">
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <button onClick={share} className="mt-2 w-full rounded-full py-2 text-[12px] tracking-[0.08em] text-[#5D00FF] border border-[#5D00FF]/25 hover:border-[#5D00FF]/60" data-testid="invite-share">
        Share link
      </button>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        <div><div className="text-[11px] text-[#676773]">Earned</div><div className="text-[16px] font-serif text-[#1C1C1C]" data-testid="invite-earned">✦ {data.earned_credits || 0}</div></div>
        <div><div className="text-[11px] text-[#676773]">Rewarded</div><div className="text-[16px] font-serif text-[#1C1C1C]" data-testid="invite-rewarded">{rewarded}</div></div>
        <div><div className="text-[11px] text-[#676773]">Pending</div><div className="text-[16px] font-serif text-[#1C1C1C]" data-testid="invite-pending">{pending}</div></div>
      </div>
      <div className="mt-2 text-[10.5px] text-[#A1A1AA]">
        Monthly cap: {rewarded}/{data.monthly_cap} rewarded this month. Bonus credits expire 90 days after they're earned.
      </div>
    </div>
  );
}

function LockedTile({ line, onIntake }) {
  return (
    <div className="space-y-5" data-testid="locked-tile">
      <p className="text-[#676773] leading-relaxed">{line}</p>
      <div className="rounded-2xl border border-amber-200/20 bg-amber-100/5 px-4 py-3 text-[13px] text-amber-100/80">
        Costs <strong>20 credits</strong> to compute your chart. You start with <strong>100</strong>.
      </div>
      <button
        onClick={onIntake}
        data-testid="locked-intake-cta"
        className="w-full rounded-full py-3 text-[14px] font-medium text-slate-50"
        style={{ background: "linear-gradient(140deg,#3B82F6,#7C3AED)", border: "1px solid rgba(223,185,110,0.5)" }}>
        Share my stars with KALKI
      </button>
    </div>
  );
}

function CharacterTile({ summary }) {
  if (!summary) return null;
  return (
    <div className="space-y-4" data-testid="character-tile">
      <Row label="Lagna rising" value={summary.lagna} />
      <Row label="Moon nakshatra" value={summary.moon_nak} />
      <Row label="Active Mahadasha" value={summary.md || "—"} />
      <p className="text-[#A1A1AA] text-[13px] leading-relaxed">
        Open <em>Future</em> or <em>Why</em> to hear what this signature is bringing into your current chapter.
      </p>
    </div>
  );
}

function HorizonTimeline({ timeline }) {
  const HZS = [
    { id: "now", label: "NOW" },
    { id: "3m",  label: "3 MONTHS" },
    { id: "1y",  label: "1 YEAR" },
    { id: "3y",  label: "3 YEARS" },
    { id: "5y",  label: "5 YEARS" },
  ];
  const H = timeline?.horizons || {};
  const AGREEMENT = {
    HIGH:    "#5BAD7E",
    MEDIUM:  "#DFB96E",
    LOW:     "#A1A1AA",
  };
  return (
    <div className="rounded-2xl border border-[#5D00FF]/15 bg-gradient-to-br from-[#5D00FF]/[0.04] to-[#FF2E9A]/[0.03] px-4 py-4 space-y-4" data-testid="horizon-timeline">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#5D00FF]/80">Future Timeline</div>
        <div className="text-[10px] text-[#A1A1AA]">as of {timeline?.today}</div>
      </div>
      <div className="space-y-3">
        {HZS.map((h) => {
          const items = (H[h.id] || []).slice(0, 3);
          if (items.length === 0) return (
            <div key={h.id} className="flex items-start gap-3" data-testid={`horizon-${h.id}`}>
              <div className="text-[10px] tracking-[0.2em] text-[#A1A1AA] shrink-0 w-20 pt-1">{h.label}</div>
              <div className="flex-1 text-[12px] text-[#A1A1AA]/70 italic">no refined window</div>
            </div>
          );
          return (
            <div key={h.id} className="flex items-start gap-3" data-testid={`horizon-${h.id}`}>
              <div className="text-[10px] tracking-[0.2em] text-[#5D00FF] shrink-0 w-20 pt-1">{h.label}</div>
              <div className="flex-1 space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="rounded-xl bg-white/60 border border-black/5 px-3 py-2" data-testid={`horizon-item-${h.id}-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-[11px] uppercase tracking-wider text-[#1C1C1C]/80">{it.label || it.domain}</div>
                      <span className="text-[9px] px-2 py-0.5 rounded-full border" style={{
                        color: AGREEMENT[it.agreement] || "#A1A1AA",
                        borderColor: (AGREEMENT[it.agreement] || "#A1A1AA") + "66",
                      }}>{it.agreement || "—"}</span>
                    </div>
                    <div className="text-[12.5px] text-[#1C1C1C] mt-1">{it.statement}</div>
                    <div className="text-[11px] text-[#676773] mt-1">
                      {it.window_start} → {it.window_end}
                      {it.double_transit_house ? ` · DT H${it.double_transit_house}` : ""}
                      {it.jaimini_trigger ? ` · ${it.jaimini_trigger}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[10.5px] text-[#A1A1AA] italic">
        Agreement grades count independent systems (Parashari + Transit + Jaimini). HIGH = three or more agree.
      </p>
    </div>
  );
}

function PredictionsTile({ domain, onCreditsChanged }) {
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [predsById, setPredsById] = useState({});   // prediction_id -> local state override
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      // Load the free Phase 3 timeline immediately (horizons view)
      try {
        const { data: tl } = await api.get("/predictions/timeline");
        if (!cancelled) setTimeline(tl);
      } catch (e) { /* non-fatal */ }
      // Load the paid rich-predictions endpoint
      try {
        const { data } = await api.post("/vedic/predict", { window_months: 24 });
        if (!cancelled) {
          setData(data);
          // B1 — hydrate predsById with any PERSISTED status so WATCHING /
          // ACCEPTED / DISMISSED survives reload.
          const seeded = {};
          for (const p of (data?.predictions || [])) {
            if (p?.prediction_id && p?.status && p.status !== "ACTIVE") {
              seeded[p.prediction_id] = { status: p.status, nextStates: [] };
            }
          }
          if (Object.keys(seeded).length) setPredsById(seeded);
          onCreditsChanged?.();
        }
      } catch (e) {
        if (!cancelled) {
          const d = e.response?.data?.detail;
          const msg = (d && typeof d === "object") ? (d.message || d.code || "Could not fetch predictions.") : (d || e.response?.data?.message || "Could not fetch predictions.");
          setError(msg);
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const transition = async (pid, newState) => {
    try {
      setSavingId(pid);
      const r = await api.post(`/predictions/${pid}/state`, { status: newState });
      setPredsById((m) => ({ ...m, [pid]: { status: r.data?.status || newState, nextStates: r.data?.transitions_from_here || [] } }));
      toast.success(`Moved to ${newState}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update state.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading && !timeline) return <p className="text-[#A1A1AA] italic">Reading your timing…</p>;
  if (error && !timeline) return <p className="text-rose-300">{error}</p>;

  const preds = data?.predictions || [];
  const cp = data?.current_periods?.vimshottari_md;

  return (
    <div className="space-y-5" data-testid="predictions-tile">
      {timeline && <HorizonTimeline timeline={timeline} />}
      {cp && (
        <div
          className="rounded-2xl px-4 py-3 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #241148 0%, #170A33 60%, #0F0624 100%)",
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 20px 60px rgba(30,0,80,0.45), 0 0 0 1px rgba(93,0,255,0.14) inset",
            color: "#F6F1FF",
          }}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] mb-1" style={{ color: "#FF9BD2" }}>Current Mahadasha</div>
          <div className="text-[15px] font-medium" style={{ color: "#F6F1FF" }}>{cp.lord}</div>
          <div className="text-[12px]" style={{ color: "rgba(230,220,255,0.65)" }}>until {cp.end_date}</div>
          <div className="text-[12px] mt-1" style={{ color: "rgba(230,220,255,0.55)" }}>
            Running chain: {data.current_periods.md}-{data.current_periods.ad}-{data.current_periods.pd}
          </div>
        </div>
      )}
      {preds.map((p) => {
        const over = predsById[p.prediction_id];
        const status = over?.status || p.status || "ACTIVE";
        return (
          <div
            key={p.prediction_id}
            data-testid={`prediction-${p.domain}`}
            className="rounded-2xl px-4 py-4 space-y-2 relative overflow-hidden"
            style={{
              background:
                "radial-gradient(120% 90% at 15% 0%, rgba(93,0,255,0.3) 0%, rgba(93,0,255,0) 55%)," +
                "radial-gradient(100% 80% at 90% 100%, rgba(255,46,154,0.2) 0%, rgba(255,46,154,0) 60%)," +
                "linear-gradient(180deg, #241148 0%, #170A33 60%, #0F0624 100%)",
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 20px 60px rgba(30,0,80,0.5), 0 0 0 1px rgba(93,0,255,0.14) inset",
              color: "#F6F1FF",
            }}
          >
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.3em]" style={{ color: "#FF9BD2" }}>{p.domain}</div>
              <ConfidenceBadge level={p.confidence} />
            </div>
            <p className="text-[14.5px] leading-relaxed" style={{ color: "#F6F1FF" }}>{p.statement}</p>
            {p.practical_implication && (
              <p className="text-[13px] italic" style={{ color: "rgba(230,220,255,0.72)" }}>{p.practical_implication}</p>
            )}

            <StateMachineRow
              status={status}
              saving={savingId === p.prediction_id}
              onTransition={(s) => transition(p.prediction_id, s)}
            />

            {p.evidence?.length > 0 && (
              <details className="mt-2 text-[12px]" style={{ color: "rgba(230,220,255,0.6)" }}>
                <summary className="cursor-pointer hover:text-white">Why I say this ({p.evidence.length})</summary>
                <ul className="mt-2 space-y-1">
                  {p.evidence.map((ev, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-[10px] uppercase tracking-wider pt-0.5 shrink-0" style={{ color: "#CFA8FF" }}>{ev.system}</span>
                      <span>{ev.statement}</span>
                    </li>
                  ))}
                </ul>
                <PredictionWhyDag pid={p.prediction_id} />
              </details>
            )}
          </div>
        );
      })}
      {data?.framing_footer && (
        <p className="text-slate-500 italic text-[11.5px] leading-relaxed">{data.framing_footer}</p>
      )}
    </div>
  );
}

/** Per-prediction state machine row — shows the lifecycle + available transitions. */
const PRED_STATE_META = {
  DRAFT:            { color: "#A1A1AA", label: "DRAFT" },
  ACTIVE:           { color: "#5D00FF", label: "ACTIVE" },
  WATCHING:         { color: "#FF2E9A", label: "WATCHING" },
  SIGNAL_DETECTED:  { color: "#DFB96E", label: "SIGNAL" },
  VERIFIED:         { color: "#5BAD7E", label: "VERIFIED" },
  PARTIAL:          { color: "#DFB96E", label: "PARTIAL" },
  NOT_OCCURRED:     { color: "#64748B", label: "DID NOT HAPPEN" },
};
const PRED_FLOW = ["DRAFT", "ACTIVE", "WATCHING", "SIGNAL_DETECTED", "VERIFIED"];
const ALLOWED_FROM = {
  DRAFT: ["ACTIVE"],
  ACTIVE: ["WATCHING", "SIGNAL_DETECTED", "VERIFIED", "NOT_OCCURRED"],
  WATCHING: ["SIGNAL_DETECTED", "VERIFIED", "PARTIAL", "NOT_OCCURRED"],
  SIGNAL_DETECTED: ["VERIFIED", "PARTIAL", "NOT_OCCURRED"],
  VERIFIED: [],
  PARTIAL: [],
  NOT_OCCURRED: [],
};

function StateMachineRow({ status, saving, onTransition }) {
  const curr = PRED_STATE_META[status] || PRED_STATE_META.ACTIVE;
  const idx = PRED_FLOW.indexOf(status);
  const nexts = ALLOWED_FROM[status] || [];
  return (
    <div className="mt-3 space-y-2" data-testid={`prediction-state-${status}`}>
      {/* Rail */}
      <div className="flex items-center gap-1.5">
        {PRED_FLOW.map((s, i) => {
          const m = PRED_STATE_META[s];
          const active = s === status;
          const past = idx >= 0 && i < idx;
          return (
            <div key={s} className="flex items-center gap-1.5 flex-1">
              <div
                className="shrink-0 rounded-full transition-all"
                style={{
                  width: active ? 12 : 8,
                  height: active ? 12 : 8,
                  background: active ? m.color : past ? m.color + "aa" : "#D4D4D8",
                  boxShadow: active ? `0 0 10px ${m.color}` : "none",
                }}
              />
              {i < PRED_FLOW.length - 1 && (
                <div
                  className="flex-1 h-[2px] rounded-full"
                  style={{ background: past ? m.color + "66" : "#E4E4E7" }}
                />
              )}
            </div>
          );
        })}
      </div>
      {/* Current + available next actions */}
      <div className="flex items-center flex-wrap gap-2 pt-1">
        <span
          className="text-[10px] font-semibold tracking-[0.25em] px-2 py-1 rounded-full border"
          style={{ color: curr.color, borderColor: curr.color + "66", background: curr.color + "0f" }}
        >
          {curr.label}
        </span>
        {nexts.map((n) => {
          const m = PRED_STATE_META[n];
          const neutral = n === "NOT_OCCURRED" || n === "PARTIAL";
          return (
            <button
              key={n}
              data-testid={`prediction-move-${n}`}
              onClick={() => onTransition(n)}
              disabled={saving}
              className="text-[10px] tracking-[0.22em] font-medium px-3 py-1.5 rounded-full border hover:-translate-y-0.5 transition-all disabled:opacity-40"
              style={neutral
                ? { color: "#676773", borderColor: "rgba(0,0,0,0.15)", background: "rgba(0,0,0,0.03)" }
                : { color: "white", borderColor: m.color, background: m.color }}
            >
              → {m.label}
            </button>
          );
        })}
        {nexts.length === 0 && (
          <span className="text-[11px] italic text-slate-500">Lifecycle complete</span>
        )}
      </div>
    </div>
  );
}

/** Per-prediction Why DAG — lightweight SVG using /vedic/explain. */
function PredictionWhyDag({ pid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Prefer the Phase 3 richer /why endpoint, fall back to /vedic/explain
      try {
        const r = await api.get(`/predictions/${pid}/why`);
        if (!cancelled && r.data?.evidence_tree) {
          setData({ ...r.data, _phase3: true }); setLoading(false); return;
        }
      } catch (e) { /* try fallback */ }
      try { const r = await api.post("/vedic/explain", { prediction_id: pid }); if (!cancelled) setData(r.data); }
      catch (e) {} finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pid]);
  if (loading) return <p className="mt-2 text-[11px] italic text-slate-500">Mapping the reasoning…</p>;
  if (!data) return null;

  // Phase 3 structured evidence tree
  if (data._phase3) {
    const tree = data.evidence_tree || [];
    const COLOR = {
      "Parashari (Natal)": "#5D00FF",
      "Vimshottari":       "#FF2E9A",
      "Timing":            "#DFB96E",
      "Transit":           "#5BAD7E",
      "Jaimini":           "#CFFF04",
      "Natal Placement":   "#5D00FF",
      "Limits":            "#A1A1AA",
    };
    return (
      <div className="mt-3 rounded-xl border border-black/5 bg-white/50 p-3" data-testid={`pred-why-dag-${pid}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[9px] tracking-[0.3em] text-[#5D00FF]">EVIDENCE TREE</div>
          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border"
                style={{ color: COLOR["Timing"], borderColor: COLOR["Timing"] + "60" }}>
            {data.agreement || "—"}
          </span>
        </div>
        <ul className="space-y-1.5">
          {tree.map((e, i) => (
            <li key={i} className="flex gap-2 text-[12px] leading-relaxed">
              <span className="text-[9.5px] uppercase tracking-wider pt-0.5 shrink-0 w-28"
                    style={{ color: COLOR[e.system] || "#676773" }}>{e.system}</span>
              <span className="text-[#1C1C1C]">{e.statement}</span>
            </li>
          ))}
        </ul>
        {data.framing_footer && (
          <p className="text-slate-500 italic text-[10.5px] mt-2 leading-relaxed">{data.framing_footer}</p>
        )}
      </div>
    );
  }

  // Legacy /vedic/explain DAG rendering
  if (!data?.prediction) return null;
  const ev = (data.prediction.evidence || []).slice(0, 4);
  const rules = (data.rules || []).slice(0, 4);
  // Sprint 2 — Prompt F: WHY graph label truncation fix.
  // Instead of hard-slicing at 36 chars we wrap into two tspans so long rule
  // names stay readable on both desktop and 900×600 laptops.
  const node = (x, y, label, color) => {
    const txt = String(label || "");
    let line1 = txt, line2 = "";
    if (txt.length > 34) {
      const breakAt = txt.indexOf(" ", 28);
      if (breakAt > 0 && breakAt < 40) {
        line1 = txt.slice(0, breakAt);
        line2 = txt.slice(breakAt + 1, breakAt + 1 + 34);
      } else {
        line1 = txt.slice(0, 32);
        line2 = txt.slice(32, 60);
      }
    }
    return (
      <g key={`node-${x}-${y}`}>
        <circle cx={x} cy={y} r="5" fill={color} style={{ filter: `drop-shadow(0 0 6px ${color})` }} />
        <text x={x + 10} y={y + 4} fontSize="10" fill="#1C1C1C">
          <tspan x={x + 10} dy="0">{line1}</tspan>
          {line2 ? <tspan x={x + 10} dy="12" fontSize="9" fill="#676773">{line2}</tspan> : null}
        </text>
      </g>
    );
  };
  const left = ev.length ? ev : [{ statement: data.prediction.domain || "Vedic signal" }];
  const H = Math.max(left.length, rules.length) * 44 + 32;
  return (
    <div className="mt-3 rounded-xl border border-black/5 bg-white/50 p-3 overflow-x-auto" data-testid={`pred-why-dag-${pid}`}>
      <div className="text-[9px] tracking-[0.3em] text-[#5D00FF] mb-1">WHY DAG</div>
      <svg viewBox={`0 0 620 ${H}`} className="w-full" preserveAspectRatio="xMidYMin meet" style={{ height: H, minWidth: 0 }}>
        <defs>
          <linearGradient id={`dag-${pid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#5D00FF" stopOpacity="0.9"/>
            <stop offset="50%" stopColor="#FF2E9A" stopOpacity="0.9"/>
            <stop offset="100%" stopColor="#CFFF04" stopOpacity="0.9"/>
          </linearGradient>
        </defs>
        <text x="20" y="14" fontSize="9" letterSpacing="2" fill="#5D00FF">EVIDENCE</text>
        <text x="260" y="14" fontSize="9" letterSpacing="2" fill="#FF2E9A">RULE</text>
        <text x="490" y="14" fontSize="9" letterSpacing="2" fill="#5BAD7E">OUTCOME</text>
        {left.map((_, i) => rules.map((__, j) => (
          <line key={`e1-${i}-${j}`} x1="90" y1={28 + i * 44} x2="330" y2={28 + j * 44}
                stroke={`url(#dag-${pid})`} strokeWidth="1" opacity="0.35"/>
        )))}
        {rules.map((_, j) => (
          <line key={`e2-${j}`} x1="330" y1={28 + j * 44} x2="550" y2={28 + Math.min(j, left.length - 1) * 44}
                stroke={`url(#dag-${pid})`} strokeWidth="1" opacity="0.5"/>
        ))}
        {left.map((e, i) => node(80, 28 + i * 44, e.statement || e.rule_id || "signal", "#5D00FF"))}
        {rules.map((r, j) => node(320, 28 + j * 44, `${r.rule_id} · ${r.name}`, "#FF2E9A"))}
        {node(540, 28, data.prediction.statement, "#5BAD7E")}
      </svg>
      {data.framing_footer && (
        <p className="text-slate-500 italic text-[10.5px] mt-2 leading-relaxed">{data.framing_footer}</p>
      )}
    </div>
  );
}

function ConfidenceBadge({ level }) {
  const map = {
    HIGH: { color: "#5BAD7E", bg: "rgba(91,173,126,0.15)", label: "HIGH" },
    MEDIUM: { color: "#DFB96E", bg: "rgba(223,185,110,0.15)", label: "MEDIUM" },
    LOW: { color: "#D97706", bg: "rgba(217,119,6,0.15)", label: "LOW" },
    INSUFFICIENT: { color: "#64748B", bg: "rgba(100,116,139,0.15)", label: "INSUFFICIENT" },
  };
  const m = map[level] || map.INSUFFICIENT;
  return (
    <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border" style={{ color: m.color, borderColor: m.color + "60", background: m.bg }} data-testid={`confidence-${level}`}>
      {m.label}
    </span>
  );
}

function WhyTile({ onCreditsChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/vedic/rules");
        if (!cancelled) setRules((r.data.rules || []).slice(0, 7));
        const { data } = await api.post("/vedic/predict", { window_months: 24 });
        if (!cancelled) { setData(data); onCreditsChanged?.(); }
      } catch (e) {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p className="text-[#A1A1AA] italic">Gathering the reasoning…</p>;
  if (!data) return null;

  // Build visual DAG: Vedic Timing → Traditional Rules → Krishna Guidance
  const lanes = [
    { id: "timing", label: "Vedic Timing", color: "#5D00FF", items: [
      data.current_periods?.vimshottari_md ? `Mahadasha ${data.current_periods.vimshottari_md.lord}` : "Mahadasha",
      data.current_periods?.md && data.current_periods?.ad ? `${data.current_periods.md}-${data.current_periods.ad}` : "Antardasha",
      "Chara MD cross-check",
      "Jupiter / Saturn transit",
    ] },
    { id: "rules", label: "Traditional Rules", color: "#FF2E9A",
      items: (rules.slice(0,4).map((r) => `${r.rule_id} · ${r.name}`)) },
    { id: "guide", label: "KALKI's Guidance", color: "#CFFF04",
      items: (data.predictions || []).slice(0,4).map((p) => p.practical_implication || p.domain) },
  ];

  return (
    <div className="space-y-5" data-testid="why-tile">
      <p className="text-[#676773] text-[14px] leading-relaxed">
        Everything I tell you rests on three independent layers. When they agree, confidence rises.
      </p>
      {/* Visual DAG */}
      <div className="rounded-2xl border border-black/5 bg-black/[0.025] p-4 overflow-x-auto" data-testid="why-dag">
        <svg viewBox="0 0 600 320" className="w-full min-w-[560px]" style={{ height: 300 }}>
          <defs>
            <linearGradient id="dagEdge" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#5D00FF" stopOpacity="0.9"/>
              <stop offset="50%" stopColor="#FF2E9A" stopOpacity="0.9"/>
              <stop offset="100%" stopColor="#CFFF04" stopOpacity="0.9"/>
            </linearGradient>
          </defs>
          {/* edges between lanes */}
          {lanes[0].items.map((_, i) => lanes[1].items.map((__, j) => (
            <line key={`e01-${i}-${j}`} x1="150" y1={40 + i*60} x2="300" y2={40 + j*60}
                  stroke="url(#dagEdge)" strokeWidth="1" opacity="0.35"/>
          )))}
          {lanes[1].items.map((_, j) => lanes[2].items.map((__, k) => (
            <line key={`e12-${j}-${k}`} x1="300" y1={40 + j*60} x2="450" y2={40 + k*60}
                  stroke="url(#dagEdge)" strokeWidth="1" opacity="0.5"/>
          )))}
          {/* lane headers */}
          {lanes.map((l, idx) => (
            <text key={l.id} x={150 + idx*150} y={20} textAnchor="middle" fontSize="10"
                  letterSpacing="2" fill={l.color} style={{textTransform:"uppercase"}}>{l.label}</text>
          ))}
          {/* nodes */}
          {lanes.map((l, idx) => l.items.map((it, i) => (
            <g key={`n-${idx}-${i}`}>
              <circle cx={150 + idx*150} cy={40 + i*60} r="6" fill={l.color}
                      style={{filter:`drop-shadow(0 0 8px ${l.color})`}}/>
              <text x={150 + idx*150} y={44 + i*60 + 18} textAnchor="middle" fontSize="10"
                    fill="#C7C7D1">{String(it).slice(0, 26)}</text>
            </g>
          )))}
        </svg>
      </div>
      <p className="text-slate-500 text-[11.5px] italic">Not just what may happen. Understand why.</p>
    </div>
  );
}

function RemediesTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try { const r = await api.get("/vedic/remedies"); if (!cancelled) setData(r.data); }
      catch (e) {}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  if (loading) return <p className="text-[#A1A1AA] italic">Preparing simple practices…</p>;
  if (!data) return null;
  return (
    <div className="space-y-5" data-testid="remedies-tile">
      <div className="rounded-2xl border border-black/5 bg-black/[0.025] px-4 py-3">
        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/80">Current MD lord · {data.md_lord}</div>
        <div className="text-[#1C1C1C] text-[14px]">Deity: {data.deity} · Mantra: {data.mantra}</div>
      </div>
      {(data.remedies || []).map((r, i) => (
        <div key={i} data-testid={`remedy-${r.remedy_type}`} className="rounded-2xl border border-black/5 bg-black/[0.025] px-4 py-4 space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-amber-200/80">{r.remedy_type}</div>
            <div className="text-[10px] text-slate-500">#{r.priority}</div>
          </div>
          <div className="text-[#1C1C1C] text-[14.5px] font-medium">{r.title}</div>
          <div className="text-[#676773] text-[13px] leading-relaxed">{r.instruction}</div>
          <div className="text-slate-500 text-[11.5px] italic">{r.rationale}</div>
        </div>
      ))}
      <p className="text-slate-500 italic text-[11.5px]">{data.note}</p>
    </div>
  );
}

// Krishna 5.0 — My Remedies: user-committed remedies (/api/remedies) with
// Done / Skip controls. Rendered inside the Me tab, below InviteFriendTile.
function MyRemediesList() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/remedies"); setItems(data.items || []); }
    catch (_) { setItems([]); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  const markDone = async (id) => {
    setBusy(id);
    try { await api.post(`/remedies/${id}/done`);
          setItems((x) => x.map((r) => r.id === id ? { ...r, status: "done" } : r)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not mark done."); }
    finally { setBusy(null); }
  };
  const markSkip = async (id) => {
    setBusy(id);
    try { await api.post(`/remedies/${id}/skip`);
          setItems((x) => x.map((r) => r.id === id ? { ...r, status: "skipped" } : r)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not skip."); }
    finally { setBusy(null); }
  };
  if (items === null) return null;
  if (items.length === 0) {
    return (
      <div data-testid="me-remedies-empty">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#676773] mb-3">My Remedies</div>
        <div className="rounded-2xl border border-black/5 bg-black/[0.025] px-5 py-5 text-center text-[13px] text-[#676773] leading-relaxed">
          KALKI has not asked you to begin a remedy yet. When a conversation surfaces one, tap <strong>Start Remedy</strong> and it will appear here.
        </div>
      </div>
    );
  }
  const active = items.filter((r) => r.status === "active");
  const closed = items.filter((r) => r.status !== "active");
  return (
    <div data-testid="me-remedies-list">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#676773]">My Remedies</div>
        <div className="text-[10px] text-[#A1A1AA]">{active.length} active · {closed.length} closed</div>
      </div>
      <div className="flex flex-col gap-2">
        {active.map((r) => (
          <div key={r.id} data-testid={`me-remedy-${r.id}`}
               className="rounded-2xl border border-black/5 bg-white/70 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] text-[#1C1C1C] leading-relaxed">{r.remedy_text || r.text}</div>
                <div className="text-[10px] tracking-wider uppercase text-[#9999A5] mt-1">
                  {r.planet ? `${r.planet} · ` : ""}{r.category || "general"}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 whitespace-nowrap">
                <button onClick={() => markDone(r.id)} disabled={busy === r.id}
                        data-testid={`me-remedy-done-${r.id}`}
                        className="text-[11px] uppercase tracking-wider text-[#5D00FF] hover:text-[#FF2E9A] disabled:opacity-40">
                  Done
                </button>
                <button onClick={() => markSkip(r.id)} disabled={busy === r.id}
                        data-testid={`me-remedy-skip-${r.id}`}
                        className="text-[11px] uppercase tracking-wider text-[#676773] hover:text-[#1C1C1C] disabled:opacity-40">
                  Skip
                </button>
              </div>
            </div>
          </div>
        ))}
        {closed.length > 0 && (
          <div className="mt-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#A1A1AA] mb-2">Closed</div>
            <div className="flex flex-col gap-1.5">
              {closed.map((r) => (
                <div key={r.id} data-testid={`me-remedy-closed-${r.id}`}
                     className="rounded-xl border border-black/5 bg-black/[0.02] px-4 py-2 text-[12.5px] text-[#676773] flex items-center justify-between gap-3">
                  <span className="truncate">{r.remedy_text || r.text}</span>
                  <span className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">{r.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl border border-black/5 bg-black/[0.025] px-4 py-3">
      <span className="text-[11px] uppercase tracking-[0.3em] text-[#A1A1AA] pt-0.5">{label}</span>
      <span className="text-[#1C1C1C] text-[14px] font-medium text-right">{value}</span>
    </div>
  );
}

function KarmaBody() {
  const [items, setItems] = useState([]);           // /karma manual items
  const [chatSteps, setChatSteps] = useState([]);   // /kalki/karma/steps (from chat)
  const [title, setTitle] = useState("");

  // B1 — load BOTH manual karma and chat-saved practical steps so the
  // Karma tab is never empty just because the user adds everything via
  // "Add to my Karma" in Chat.
  const refresh = useCallback(async () => {
    try { const { data } = await api.get("/karma"); setItems(data.items || []); } catch (_) {}
    try { const { data } = await api.get("/kalki/karma/steps", { params: { limit: 100 } });
          setChatSteps(data.items || []); } catch (_) {}
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async () => {
    if (!title.trim()) return;
    try { const { data } = await api.post("/karma", { title: title.trim() }); setItems((x) => [data, ...x]); setTitle(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add action."); }
  };
  const markDone = async (id) => {
    try { await api.post(`/karma/${id}/done`); setItems((x) => x.map((it) => (it.id === id ? { ...it, status: "done" } : it))); }
    catch (e) {}
  };
  const markStepDone = async (id) => {
    try { await api.post(`/kalki/karma/steps/${id}/done`);
          setChatSteps((x) => x.map((s) => s.id === id ? { ...s, status: "done" } : s)); }
    catch (e) {}
  };
  const active = items.filter((i) => i.status === "active");
  const activeSteps = chatSteps.filter((s) => s.status !== "done" && s.status !== "skipped");
  const doneSteps = chatSteps.filter((s) => s.status === "done");

  return (
    <div className="space-y-5" data-testid="karma-tab">
      <p className="text-[#676773] text-[14px]">I ask you to carry no more than three steps at a time. Finish one, and I will give you the next.</p>

      {/* Chat-saved practical steps (B1 — the "Add to my Karma" taps) */}
      {activeSteps.length > 0 && (
        <div className="space-y-2" data-testid="karma-chat-steps">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#676773]">From chat</div>
          {activeSteps.map((s) => (
            <div key={s.id} className="flex items-start justify-between gap-3 rounded-2xl border border-black/5 bg-white/70 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="text-[14px] text-[#1C1C1C] leading-relaxed">{s.text}</div>
                {s.category && <div className="text-[10px] tracking-wider uppercase text-[#9999A5] mt-1">{s.category}</div>}
              </div>
              <button onClick={() => markStepDone(s.id)} data-testid={`karma-step-done-${s.id}`}
                      className="text-[11px] uppercase tracking-wider text-[#5D00FF] hover:text-[#FF2E9A] whitespace-nowrap">
                Done
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {active.length === 0 && activeSteps.length === 0 && (
          <p className="text-slate-500 italic text-[13px]" data-testid="karma-empty">There is nothing I need you to chase right now. Come, let us talk.</p>
        )}
        {active.map((it) => (
          <div key={it.id} className="flex items-center justify-between rounded-2xl border border-black/5 bg-black/[0.025] px-4 py-3">
            <div className="text-[14px] text-[#1C1C1C]">{it.title}</div>
            <button onClick={() => markDone(it.id)} data-testid={`karma-done-${it.id}`} className="text-[11px] uppercase tracking-wider text-amber-300 hover:text-amber-200">Done</button>
          </div>
        ))}
      </div>
      {active.length < 3 && (
        <div className="flex items-center gap-2">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a step for today…"
                 data-testid="karma-input" className="flex-1 rounded-full bg-black/[0.025] border border-black/5 px-4 py-2.5 text-[14px] text-[#1C1C1C]" />
          <button onClick={add} data-testid="karma-add" className="rounded-full px-4 py-2.5 text-[13px] text-slate-50"
                  style={{ background: "linear-gradient(140deg,#5D00FF,#FF2E9A)", border: "1px solid rgba(207,255,4,0.4)" }}>Add</button>
        </div>
      )}

      {doneSteps.length > 0 && (
        <div className="pt-3 border-t border-black/5 space-y-2" data-testid="karma-done-list">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#9999A5]">Completed</div>
          {doneSteps.slice(0, 6).map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-[12px] text-[#676773]">
              <span>✓</span><span className="line-through">{s.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function JourneyBody() {
  const [refl, setRefl] = useState([]);
  const [karma, setKarma] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/reflections");
        const k = await api.get("/karma");
        setRefl(r.data.items || []);
        setKarma((k.data.items || []).filter((i) => i.status === "done"));
      } catch (e) {}
    })();
  }, []);
  const combined = [
    ...refl.map((r) => ({ type: "reflection", t: r.created_at, title: r.question, body: r.takeaway })),
    ...karma.map((k) => ({ type: "karma", t: k.completed_at || k.created_at, title: "Completed", body: k.title })),
  ].sort((a, b) => new Date(b.t) - new Date(a.t));

  if (combined.length === 0) return (
    <div className="space-y-4">
      <p className="text-[#676773] leading-relaxed">Every journey begins with one conversation. Start talking to me and I will begin to remember what we walked through together.</p>
    </div>
  );
  return (
    <div className="relative pl-5">
      <div aria-hidden className="absolute left-1.5 top-2 bottom-2 w-[1px]" style={{ background: "linear-gradient(180deg, transparent, rgba(223,185,110,0.5), transparent)" }} />
      <div className="space-y-6">
        {combined.map((e, idx) => (
          <div key={idx} className="relative">
            <span className="absolute -left-[22px] top-1.5 w-3 h-3 rounded-full"
                  style={{ background: e.type === "reflection" ? "#DFB96E" : "#7C3AED", boxShadow: "0 0 10px rgba(223,185,110,0.5)" }} />
            <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-1">
              {new Date(e.t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} · {e.type}
            </div>
            <div className="text-[#1C1C1C] text-[14px] font-medium mb-1">{e.title}</div>
            <div className="text-[#676773] text-[13.5px] leading-relaxed">{e.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
